import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import _, { get } from 'lodash'
import styled, { css } from 'styled-components'
import { Button, Modal, Checkbox, Input, Select } from 'antd'
import axios from 'axios'
import InfiniteScroll from 'react-infinite-scroll-component'
import { CloseOutlined } from '@ant-design/icons'
import { ViewIcon, SimpleUploadIcon } from '../icons' // BellIcon
import Wrapper from './wrapper'
import Banner from './comp/banner'
import Cardiometabolic from './sections/cardiometabolic'
import Aerobic from './sections/aerobic'
import MovementQuality from './sections/movementQuality'
import CognitiveHealth from './sections/cognitiveHealth'
import PageLoading from '../UI/status/loading'
import IntroductionSection from './sections/IntroductionSection'
import HealthCompassSection from './sections/HealthCompassSection'
import PersonalInfoSection from './sections/PersonalInfoSection'
import AspirationsGoalsSection from './sections/AspirationsGoalsSection'
import BoneHealth from './sections/BoneHealth'
import BodyCompResultSection from './sections/BodyCompResultSection'
import Balance from './sections/balance'
import MuscleSection from './sections/MuscleSection'
import RecommendationSectionV1 from './sections/RecommendationSectionv1'
import RecommendationSection from './sections/RecommendationSection'
import LifeStylePresentation from './LifeStylePresentation'
import Menopause from './sections/Menopause'
import { askAIAboutClient } from '../../api'
import { getClient } from '../../utils'
import { isDarkDeck } from '../../utils/constant'

const { Option } = Select

export default Wrapper(DraftReport)

export const DraftContainer = styled.div`
  font-family: Gilroy;
  padding-bottom: 68px;
  section {
    padding: 48px 200px;
    background: #ffffff;
    margin-bottom: 20px;
    .section-title {
      color: #4c6072;
      font-size: 32px;
      letter-spacing: 0.3px;
      text-transform: capitalize;
      line-height: 150%;
      padding: 0;
      padding-bottom: 20px;
      border-bottom: 1px solid #e3e6ea;
      font-weight: 500;
    }
    .sub-title {
      margin-top: 32px;
      margin-bottom: 16px;
      font-size: 18px;
      color: #4c6072;
      letter-spacing: 0.3px;
      text-transform: capitalize;
      display: flex;
      justify-content: space-between;
    }
  }

  /* Central branded (dark) theme. Applies the branded surface to every
     section rendered inside the report - including sections that don't
     define their own branded styles (Bone Health, Body Composition,
     Menopause, etc.) - and flips shared light elements (antd cards,
     inputs, dividers, HealthCard) to the dark palette. */
  ${(props) =>
    props.branded &&
    css`
      && section {
        background: #0a0a0a;
        color: #a3a3a3;
      }

      && .section-title {
        color: #ffffff;
        border-bottom-color: #252525;
      }

      && .sub-title {
        color: #a3a3a3;
      }

      && section .ant-divider {
        background: #252525;
      }

      /* antd cards (used by HealthCard and others) */
      && section .ant-card {
        background: transparent;
        color: #a3a3a3;
        border-color: #252525;
      }

      /* HealthCard */
      && section .health-card {
        border-color: #252525;
        header .name {
          color: #ffffff;
        }
        .ant-input {
          background-color: #161616;
          color: #a3a3a3;
        }
        .ant-card-meta-description .actions {
          border-top-color: #252525;
          color: #c5beb3;
          .btn.disabled {
            color: #6b6b6b;
          }
        }
      }
      /* HealthCard summary text: antd applies
         .ant-card-meta-description { color: rgba(0,0,0,0.45) } directly on
         the element, which is dark/invisible on the branded surface */
      && section .health-card .ant-card-meta-description,
      && section .health-card .description {
        color: #a3a3a3;
      }
      /* previous-score readout + previous-score input are #0d2350 (dark
         navy) and the "/" separator is at opacity 0.1 — all invisible on
         the dark surface */
      && section .health-card header .compare-data {
        color: #a3a3a3;
      }
      && section .health-card .meta-content .ant-input,
      && section .health-card .meta-content .ant-input:last-child {
        background-color: #161616;
        color: #a3a3a3;
      }
      && section .health-card .split {
        opacity: 0.4;
      }

      /* antd inputs / textarea */
      && section .ant-input {
        background-color: #161616;
        color: #a3a3a3;
        border-color: #252525;
      }

      /* shared range legend lists */
      && section .range-list {
        color: rgba(197, 190, 179, 0.5);
      }

      /* comment / note blocks (extraInformation) */
      && section .information-editor {
        border-color: #c5beb3;
        .mark-down-support {
          background: #161616;
          color: #a3a3a3;
          border-color: #252525;
          .direction-label,
          span {
            color: #c5beb3;
            border-bottom-color: #c5beb3;
          }
        }
      }
      && section .information-container {
        color: #c5beb3;
        border-color: #c5beb3;
        &.Opened,
        &.Sent,
        &.Locked {
          background: rgba(197, 190, 179, 0.08);
          color: #a3a3a3;
        }
        .content {
          color: #a3a3a3;
        }
        /* markdown headings inside saved notes are injected with inline
           color #383E48 — beat the inline style so they read on dark */
        .content h1,
        .content h2,
        .content h3,
        .content h4,
        .content h5,
        .content h6,
        .content strong {
          color: #e6e2da !important;
        }
        .content a {
          color: #c5beb3 !important;
        }
      }

      /* ---- low-level metric cards (LowleveMetrics) ----
         Same dark styling the blood panel (cardiometabolic) section
         applies, applied centrally so every section matches. */
      && section .section-name,
      && section .display-name {
        color: #e6e2da;
      }
      && section .bar-wrap,
      && section .tab-div,
      && section .ranges-info {
        background: #141414;
        border-color: #2a2a2a;
      }
      && section .tab-background {
        background: #141414;
      }
      && section .range-title,
      && section .range-level {
        color: #e6e2da;
      }
      && section .range-info-container {
        color: #a3a3a3;
      }
      && section .progress-container {
        background: transparent;
      }
      && section .range-info .name {
        color: #a3a3a3;
        opacity: 1;
      }
      && section .ant-tabs-tab-btn {
        color: #a3a3a3;
      }
      && section .ant-tabs-tab-active .ant-tabs-tab-btn {
        color: #c5beb3;
      }
      && section .ant-tabs-ink-bar {
        background: #c5beb3;
      }
      && section .tab-scroll,
      && section .tab-scroll p,
      && section .tab-scroll li,
      && section .tab-scroll div {
        color: #a3a3a3;
      }

      /* ---- body composition score items (metric titles above range bars
         + metric value rows) — same label/value colors as the blood panel ---- */
      && section .score-item {
        color: #e6e2da;
        border-bottom-color: #252525;
      }
      && section .score-item .h-item {
        color: #e6e2da;
      }
      /* Previous / Age-Ranking / Desired captions */
      && section .score-item .r-item {
        color: #a3a3a3;
      }
      /* big metric numbers that don't carry a green/red status class */
      && section .score-item .value:not(.green):not(.red) {
        color: #c5beb3;
      }

      /* ---- shared RangeBar texts (body comp, bone health DXA, muscle,
         balance, aerobic) ----
         .range-area and the value numbers carry inline #4c6072, so they
         need !important to override */
      && section .range-area {
        color: #e6e2da !important;
      }
      && section .range-item .value span,
      && section .range-item .value-number {
        color: #e6e2da !important;
      }
      /* level caption sitting on the range strip (has-value .level) */
      && section .range-item .has-value .level {
        color: #e6e2da;
      }

      /* muscle fitness sub-section names */
      && section .metrics-section-name {
        color: #e6e2da;
      }

      /* strength/muscle bar charts shared legend + value rows.
         StrengthContainer's .range-list / .metric-info rules beat the generic
         section rules on specificity, so re-assert them here. */
      && section .range-list {
        color: rgba(197, 190, 179, 0.6);
      }
      && section .metric-info {
        color: #a3a3a3;
      }
      && section .metric-info .currently {
        color: #e6e2da;
      }
      /* (L) / (R) / TOTAL / (AVG) labels next to the compare bars */
      && section .bar-with-title > span {
        color: #a3a3a3;
      }
      /* muscle metric titles: Grip Strength (L)/(R), single-text titles,
         body-chart titles + fallback values (no status color).
         The base rules are deeply nested (up to 0,7,0), so these use the
         full path to win. Status colors (green/red inline) are untouched. */
      && section .metric-content .metric-middle-container .sub-metrics-container .metric-text-container .single-text-metric .single-text-metric-title,
      && section .metric-middle-container .sub-metrics-container .metric-text-container .single-text-metric .single-text-metric-title,
      && section .single-text-metric-title {
        color: #e6e2da;
      }
      && section .metric-name,
      && section .body-chart-metric-title,
      && section .polar-chart-name {
        color: #e6e2da;
      }
      /* movement-quality / body-comp RangeBar names, e.g.
         "Cervical Lateral Flexion Right" — RangeBarContainer's
         "color: #4c6072" is inherited (no direct color on .name) */
      && section .ranges .name,
      && section .ranges .name span {
        color: #e6e2da;
      }

      /* ---- illustrations: monochrome blue line-art figures
         (skeleton / exercise movement poses, body diagrams, menopause
         figure) → white line-art on the dark surface.
         Targeted containers only — never invert uploads / DXA scans ---- */
      && section .metric-image-conatiner img,
      && section .balance-layout .image > img,
      && section .image-container img,
      && section .image-around img,
      && section .female-sprint-img {
        filter: brightness(0) invert(1);
        opacity: 0.8;
      }
      /* ---- branded (marketing-site) view: SVG files in /branded/
         already have white stroke paths, so don't re-invert them ---- */
      && section .metric-image-conatiner img[src*="/branded/"],
      && section .balance-layout .image > img[src*="/branded/"],
      && section .image-container img[src*="/branded/"],
      && section .image-around img[src*="/branded/"],
      && section .female-sprint-img[src*="/branded/"] {
        filter: none;
        opacity: 1;
      }

      /* body-composition figure labels (Right Arm % Fat, Trunk % Fat,
         Left Leg % Fat, etc.) and their values — inherited dark
         #4c6072 is unreadable on the dark surface */
      && section .image-around .label,
      && section .image-around .level {
        color: #e6e2da;
      }

      /* victory chart labels (muscle history chart) are inline-styled
         #4C6072 — override with !important */
      && section .metric-middle-container svg text {
        fill: #a3a3a3 !important;
      }

      /* menopause score headers + chart tooltips */
      && section .score-header {
        color: #a3a3a3;
      }
      && section .custom-tooltip,
      && section .custom-tooltip-container {
        background: #161616;
        color: #a3a3a3;
        border: 1px solid #252525;
      }

      /* Exercise Scheduler modal - dark overlay + content */
      && .exercise-scheduler-modal-branded .ant-modal-content {
        background-color: #0d0d0d;
        border: 1px solid #252525;
        border-radius: 12px;
      }
      && .exercise-scheduler-modal-branded .ant-modal-body {
        background-color: #0d0d0d;
        padding: 0;
      }
      && .exercise-scheduler-modal-branded .ant-modal-close {
        color: #e6e2da;
      }
      && .exercise-scheduler-modal-branded .ant-modal-close:hover {
        color: #ffffff;
        background-color: #252525;
      }
    `}
`

const ErrorContainer = styled.div`
  display: flex;
  margin-top: 112px;
  font-size: 18px;
  font-family: Gilroy;
  color: #323f4a;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .message {
    margin-bottom: 8px;
  }
`

export const Footer = styled.footer`
  width: 1440px;
  max-width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 17px 200px;
  position: fixed;
  bottom: 0;
  background: #fcfdfd;
  box-shadow: 0px -10px 10px rgba(51, 41, 41, 0.05);
  div > button {
    margin-left: 10px;
  }
  div > button:first-child {
    margin-left: 0;
  }

  ${(props) =>
    props.branded &&
    css`
      background: #0a0a0a;
      box-shadow: 0px -10px 10px rgba(0, 0, 0, 0.4);

      div > button {
        border-color: #c5beb3;
        color: #c5beb3;
        &:hover,
        &:focus {
          border-color: #ffffff;
          color: #ffffff;
        }
      }

      div > button.ant-btn-primary:not(.ant-btn-ghost) {
        background: #ffffff;
        border-color: #ffffff;
        color: #0a0a0a;
      }
    `}
`

const AskAIFloatingButton = styled(Button)`
  position: fixed;
  right: 24px;
  bottom: 92px;
  z-index: 1000;
  border-radius: 999px;
  box-shadow: 0px 8px 24px rgba(38, 66, 130, 0.25);
`

const AskAIPanel = styled.div`
  position: fixed;
  top: 64px;
  right: 0;
  width: 420px;
  max-width: 100%;
  height: calc(100vh - 64px);
  background: #ffffff;
  border-left: 1px solid #e5eaf0;
  box-shadow: -8px 0px 24px rgba(50, 63, 74, 0.12);
  z-index: 1001;
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #e5eaf0;
    color: #264382;
    font-weight: 600;
  }

  .messages {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px;
    background: #f8fafc;
  }

  .msg {
    margin-bottom: 12px;
    max-width: 90%;
    padding: 10px 12px;
    border-radius: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.5;
  }

  .msg.user {
    margin-left: auto;
    background: #264382;
    color: #fff;
    border-bottom-right-radius: 4px;
  }

  .msg.ai {
    margin-right: auto;
    background: #fff;
    color: #334e68;
    border: 1px solid #e5eaf0;
    border-bottom-left-radius: 4px;
  }

  .msg .msg-image {
    display: block;
    margin-top: 10px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5eaf0;
  }

  .msg .msg-image-error {
    padding: 8px 10px;
    font-size: 12px;
    color: #b44b4b;
    background: #fff6f6;
    border: 1px dashed #e6b3b3;
  }

  .msg .msg-image-src {
    display: block;
    margin-top: 4px;
    word-break: break-all;
    color: #829ab1;
  }

  .composer {
    border-top: 1px solid #e5eaf0;
    padding: 12px;
    background: #fff;
  }

  .composer-actions {
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .composer-mode {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #627d98;
    user-select: none;
  }

  .composer-mode .ant-select {
    min-width: 96px;
  }
`

const AI_TEXT_FALLBACK = 'I could not generate a response right now.'

const normalizeAIResponseText = (response, options = {}) => {
  const { fallback, stringify = true } = options
  const fallbackText = fallback === undefined ? AI_TEXT_FALLBACK : fallback
  if (!response) return fallbackText
  if (typeof response === 'string') return response

  // Only primitives are usable as chat text: `result` is often the image
  // payload, and stringifying an object would render "[object Object]".
  const candidates = [
    response.answer,
    response.response,
    response.result,
    get(response, 'data.answer'),
    get(response, 'data.response'),
    get(response, 'data.result'),
    get(response, 'data.message'),
    get(response, 'message')
  ].filter((item) => typeof item === 'string' || typeof item === 'number')

  if (candidates.length) return String(candidates[0])

  if (Array.isArray(response.matches) && response.matches.length) {
    return response.matches
      .map((item) => item.text || item.answer || item.content)
      .filter((item) => typeof item === 'string' || typeof item === 'number')
      .join('\n\n')
  }

  // Skipped for image answers, otherwise the whole payload (base64 included)
  // would be dumped into the bubble as text.
  if (stringify) {
    try {
      return JSON.stringify(response.data || response)
    } catch (e) {
      return 'I got a response, but could not parse it.'
    }
  }

  return fallbackText
}

/**
 * Pulls the generated image out of a chat response. The sandbox may return a
 * hosted url, a base64 payload, an images/attachments array or a bare data url.
 */
const firstString = (paths, response) => {
  for (let i = 0; i < paths.length; i++) {
    const value = get(response, paths[i])
    if (typeof value === 'string' && value) return value
  }
  return ''
}

const AI_IMAGE_URL_PATHS = [
  // Inline images come back as a complete data url in `data_uri` (with
  // `hosted: false` and `url: null`), so it has to be checked first.
  'data_uri',
  'image.data_uri',
  'data.data_uri',
  'result.data_uri',
  'data.result.data_uri',
  'output.data_uri',
  'data.output.data_uri',
  'image_url',
  'image.url',
  'data.image_url',
  'data.image.url',
  'result.image_url',
  'result.image.url',
  'data.result.image_url',
  'data.result.image.url',
  'output.image_url',
  'output.image.url',
  'data.output.image_url',
  'data.output.image.url',
  'response.image_url',
  'message.image_url',
  // `url` is a very generic key, so it is checked last: when the sandbox hosts
  // the file it returns `hosted: true` with `url` set and `data_uri` empty.
  'url',
  'data.url',
  'result.url',
  'data.result.url',
  'output.url',
  'response.url',
  'message.url'
]

const AI_IMAGE_BASE64_PATHS = [
  'image_base64',
  'image.base64',
  'data.image_base64',
  'data.image.base64',
  'result.image_base64',
  'result.image.base64',
  'data.result.image_base64',
  'output.image_base64',
  'output.image.base64',
  'data.output.image_base64'
]

const AI_IMAGE_MIME_PATHS = [
  // The sandbox names the mime `content_type` (e.g. "image/png").
  'content_type',
  'data.content_type',
  'image.content_type',
  'data.image.content_type',
  'result.content_type',
  'output.content_type',
  'data.output.content_type',
  'mime_type',
  'mimeType',
  'image.mime_type',
  'data.mime_type',
  'data.image.mime_type',
  'result.mime_type',
  'result.image.mime_type',
  'data.result.mime_type',
  'output.mime_type',
  'data.output.mime_type'
]

// Every array the sandbox may hand the pictures back in.
const AI_IMAGE_ARRAY_PATHS = [
  'images',
  'data.images',
  'result.images',
  'data.result.images',
  'attachments',
  'data.attachments',
  'output.images',
  'response.images'
]

// Keys that can hold the payload of a single entry of such an array. Providers
// disagree on the name: openai image api -> b64_json, google -> inlineData,
// anthropic -> source, and the sandbox itself -> a bare string / data url.
const AI_IMAGE_ENTRY_URL_KEYS = [
  'data_uri',
  'image.data_uri',
  'data.data_uri',
  'url',
  'image_url',
  'src',
  'path',
  'uri',
  'href',
  'image.url',
  'image.image_url',
  'data.url',
  'data.image_url'
]

const AI_IMAGE_ENTRY_BASE64_KEYS = [
  'b64_json',
  'base64',
  'data',
  'image_base64',
  'bytes_b64',
  'bytes_base64',
  'image.base64',
  'image.data',
  'image.b64_json',
  'source.data',
  'source.base64',
  'source.b64_json',
  'inlineData.data',
  'data.base64',
  'data.image_base64'
]

const AI_IMAGE_ENTRY_MIME_KEYS = [
  'content_type',
  'data.content_type',
  'image.content_type',
  'mime_type',
  'mimeType',
  'image.mime_type',
  'source.media_type',
  'inlineData.mimeType',
  'data.mime_type'
]

const AI_IMAGE_DEFAULT_MIME = 'image/png'
// A real base64 image payload is orders of magnitude bigger than any url, so
// the length is a safe guard before treating a string as image bytes.
const AI_IMAGE_MIN_BASE64_LENGTH = 256
const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/

/**
 * Turns any image payload (hosted url, data url or bare base64) into a usable
 * `src`. Bare base64 must be wrapped in a data url, otherwise the browser
 * resolves it as a relative path and the image silently fails to render.
 */
const toImageSrc = (value, mimeType) => {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.indexOf('data:image/') === 0) return trimmed

  const compact = trimmed.replace(/\s+/g, '')
  if (
    compact.length >= AI_IMAGE_MIN_BASE64_LENGTH &&
    BASE64_PATTERN.test(compact)
  ) {
    return `data:${mimeType || AI_IMAGE_DEFAULT_MIME};base64,${compact}`
  }

  return trimmed
}

/**
 * Resolves one entry of an images/attachments array, whatever the provider
 * named the field holding the bytes.
 */
const readImageEntry = (entry) => {
  if (typeof entry === 'string') return toImageSrc(entry)
  if (!entry || typeof entry !== 'object') return null

  const declaredType = String(get(entry, 'type') || '')
  const mimeType =
    firstString(AI_IMAGE_ENTRY_MIME_KEYS, entry) ||
    (declaredType.indexOf('image/') === 0 ? declaredType : '') ||
    AI_IMAGE_DEFAULT_MIME

  for (let i = 0; i < AI_IMAGE_ENTRY_URL_KEYS.length; i++) {
    const url = toImageSrc(get(entry, AI_IMAGE_ENTRY_URL_KEYS[i]), mimeType)
    if (url) return url
  }

  for (let i = 0; i < AI_IMAGE_ENTRY_BASE64_KEYS.length; i++) {
    const base64 = toImageSrc(get(entry, AI_IMAGE_ENTRY_BASE64_KEYS[i]), mimeType)
    if (base64) return base64
  }

  return null
}

/**
 * Pulls the generated images out of a chat response. The sandbox may return a
 * hosted url, a `data_uri`, a base64 payload, an images/attachments array or a
 * bare data url. Every source is merged (instead of returning on the first
 * hit) so a payload carrying both the top level image and an `images` array
 * with more than one picture renders all of them, de-duplicated.
 */
const extractAIImages = (response) => {
  if (!response) return []

  if (typeof response === 'string') {
    const single = toImageSrc(response)
    return single ? [single] : []
  }

  const mimeType =
    firstString(AI_IMAGE_MIME_PATHS, response) || AI_IMAGE_DEFAULT_MIME

  const sources = []
  const push = (src) => {
    if (src && sources.indexOf(src) === -1) sources.push(src)
  }

  push(toImageSrc(firstString(AI_IMAGE_URL_PATHS, response), mimeType))
  push(toImageSrc(firstString(AI_IMAGE_BASE64_PATHS, response), mimeType))

  for (let i = 0; i < AI_IMAGE_ARRAY_PATHS.length; i++) {
    const list = get(response, AI_IMAGE_ARRAY_PATHS[i])
    if (!Array.isArray(list) || !list.length) continue

    list.forEach((entry) => push(readImageEntry(entry)))

    if (sources.length) break

    console.warn(
      '[AskAI] images payload could not be read, keys:',
      list.map((entry) =>
        entry && typeof entry === 'object' ? Object.keys(entry) : typeof entry
      )
    )
  }

  return sources
}

/**
 * Normalizes a chat answer into a text + optional image pair.
 * An image-only answer is valid, so it must not fall back to the error text.
 */
const normalizeAIResponse = (response) => {
  const imageUrls = extractAIImages(response)

  if (!imageUrls.length) {
    return { text: normalizeAIResponseText(response), imageUrl: null, imageUrls: [] }
  }

  const text =
    typeof response === 'string'
      ? ''
      : normalizeAIResponseText(response, { fallback: '', stringify: false })

  return { text, imageUrl: imageUrls[0], imageUrls }
}

function DraftReport(props) {
  const {
    list,
    person,
    loginRole,
    arDetail,
    latest_bmi,
    latest_height,
    latest_weight,
    device_info,
    residence,
    dynamicData,
    handlePublish,
    handlePreview,
    handleSave,
    showActivity,
    // handleNotify,
    handleCommendSave,
    HandleSaveScore,
    updateEditingCount,
    editingCount,
    createdFailed,
    ageScopes,
    infinityLoadCount,
    maxLoadCount,
    next,
    hideSectionHandle,
    hideSection,
    trendMetrics,
    onUpdateReferral,
    updateArDetail,
    exerciseRecommendationPlots,
    copilotDashboard,
    executiveSummary,
    useAspirationAndGoals,
    useAIRecommendations,
    handleThemeChange,
    themeChanging
  } = props

  const previous = get(dynamicData, 'healthSpanDomainSummary.1', {})
  const latest = get(dynamicData, 'healthSpanDomainSummary.0', {})
  const hasMore = infinityLoadCount < maxLoadCount
  const branded = isDarkDeck()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const clientGender = get(getClient(), 'profile.gender', '')
 
  const getDataList = (snapshot, graph_type) => {
    return list.map((item) => {
      // const catchScore = catchEditScore.find(
      //   (score) => score.type === item.type && score.graph_type === graph_type
      // )
      const detail =
        (snapshot[item.type] && _.cloneDeep(snapshot[item.type])) || {}
      detail.isDefaultValue = true
      if (detail.score_manual && detail.score_manual.value) {
        detail.isDefaultValue = false
      }
      // if (catchScore) {
      //   detail.score_manual = Object.assign(detail.score_manual || {}, {
      //     value: catchScore.score,
      //     updated_at: new Date()
      //   })
      // }
      return {
        ...item,
        ...detail
      }
    })
  }

  const compareData = getDataList(previous.snapshot || {}, 'previous')
  const dataList = getDataList(latest.snapshot || {}, 'latest')

  let [catchEditScore, setCatchEditScore] = useState([])
  const [scoresLegalCheck, serScoresLegalCheck] = useState({})
  const [isAIChatOpen, setIsAIChatOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  // Tells the AI sandbox whether this question expects an image or text.
  const [aiGenerateImage, setAiGenerateImage] = useState(true)
  const [aiMessages, setAiMessages] = useState([
    {
      role: 'ai',
      text: 'Hi! Ask me anything about this Assessment Review draft.',
      greeting: true
    }
  ])
  // Images that resolved to a src the browser refused to load (bad host, 401 on
  // an auth protected url, expired presigned link...). Surfaced in the bubble so
  // the failing src is visible instead of a silently blank area.
  const [aiImageErrors, setAiImageErrors] = useState({})

  const onAIImageError = (key, src) => {
    console.warn('[AskAI] image failed to load, src =', src)
    setAiImageErrors((prev) => ({ ...prev, [key]: true }))
  }

  const saveBulk = async (catchData) => {
    await HandleSaveScore(catchData || catchEditScore)
    setCatchEditScore([])
  }

  const save = async (type, setToDefaultValue) => {
    if (setToDefaultValue) {
      await saveBulk([
        {
          type,
          score: null,
          graph_type: 'latest'
        },
        {
          type,
          score: null,
          graph_type: 'previous'
        }
      ])
    } else if (catchEditScore.length) {
      await saveBulk()
    }
  }
  const onScoreChangeHandle = (score, type, graph_type) => {
    score = score || null
    let targetIndex = catchEditScore.findIndex(
      (item) => item.type === type && item.graph_type === graph_type
    )
    const scoreLegal = score ? score >= 5 && score <= 99 : true
    const scoreKey = type + graph_type
    scoresLegalCheck[scoreKey] = scoreLegal

    if (targetIndex !== -1) {
      catchEditScore[targetIndex].score = score
    } else {
      catchEditScore.push({
        type,
        graph_type,
        score
      })
    }
    setCatchEditScore(catchEditScore)
    serScoresLegalCheck(scoresLegalCheck)
  }
  const cancelEdit = (type) => {
    if (type) {
      catchEditScore = catchEditScore.filter((item) => item.type !== type)
      setCatchEditScore(catchEditScore)
    } else {
      setCatchEditScore([])
    }
  }

  const commonProps = {
    hideSectionHandle,
    hideSection,
    person,
    dynamicData,
    onScoreChangeHandle,
    cancelEdit,
    updateEditingCount,
    loginRole,
    handleCommendSave,
    arDetail,
    view: 'Draft'
  }

  // Image-only answers carry no text, so they drop out of the history here.
  const buildAIHistory = (messages) =>
    messages
      .filter(
        (item) =>
          !item.greeting && item.text && (item.role === 'user' || item.role === 'ai')
      )
      .map((item) => ({
        role: item.role === 'ai' ? 'assistant' : 'user',
        content: item.text
      }))

  const onAskAI = async () => {
    const prompt = (aiPrompt || '').trim()
    if (!prompt || aiLoading) return

    const history = buildAIHistory(aiMessages)

    setAiMessages((prev) => [...prev, { role: 'user', text: prompt }])
    setAiPrompt('')
    setAiLoading(true)
    try {
      const result = await askAIAboutClient({
        prompt,
        client_id: get(person, 'id'),
        history,
        generate_image: aiGenerateImage
      })

      const normalized = normalizeAIResponse(result)
      console.info(
        '[AskAI] answer images extracted:',
        normalized.imageUrls.length,
        '| payload keys:',
        Object.keys(result || {})
      )
      setAiMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: normalized.text,
          imageUrl: normalized.imageUrl,
          imageUrls: normalized.imageUrls
        }
      ])
    } catch (error) {
      const detail = get(error, 'detail')
      setAiMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: detail
            ? `Sorry — ${detail}`
            : 'Sorry, I could not fetch an answer right now. Please try again.'
        }
      ])
    } finally {
      setAiLoading(false)
    }
  }

  return arDetail ? (
    <InfiniteScroll
      dataLength={infinityLoadCount}
      next={next}
      hasMore={infinityLoadCount < maxLoadCount}
      loader={
        <h4
          style={{
            paddingBottom: 75,
            fontSize: 24,
            textAlign: 'center',
            color: branded ? '#a3a3a3' : '#4C6072'
          }}
        >
          Loading...
        </h4>
      }
      endMessage={
        <p style={{ textAlign: 'center', color: branded ? '#a3a3a3' : undefined }}>
          <b>Yay! You have seen it all</b>
        </p>
      }
    >
      <DraftContainer branded={branded}>
        <AskAIFloatingButton
          type="primary"
          onClick={() => setIsAIChatOpen(true)}
        >
          Ask AI
        </AskAIFloatingButton>
        {isAIChatOpen && (
          <AskAIPanel>
            <div className="header">
              <span>Ask AI</span>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setIsAIChatOpen(false)}
              />
            </div>
            <div className="messages">
              {aiMessages.map((item, index) => {
                const images =
                  item.imageUrls && item.imageUrls.length
                    ? item.imageUrls
                    : item.imageUrl
                    ? [item.imageUrl]
                    : []

                return (
                  <div className={`msg ${item.role}`} key={`ai-msg-${index}`}>
                    {item.text}
                    {images.map((src, imgIndex) => {
                      const imageKey = `ai-msg-${index}-img-${imgIndex}`

                      if (aiImageErrors[imageKey]) {
                        return (
                          <div className='msg-image msg-image-error' key={imageKey}>
                            Image could not be loaded
                            <span className='msg-image-src'>
                              {src.slice(0, 160)}
                            </span>
                          </div>
                        )
                      }

                      return (
                        <img
                          src={src}
                          alt='AI generated visual'
                          className='msg-image'
                          key={imageKey}
                          onError={() => onAIImageError(imageKey, src)}
                        />
                      )
                    })}
                  </div>
                )
              })}
            </div>
            <div className="composer">
              <Input.TextArea
                value={aiPrompt}
                autoSize={{ minRows: 3, maxRows: 5 }}
                placeholder={
                  aiGenerateImage
                    ? "Describe the image you want generated from this client's data..."
                    : "Ask about this client's AR deck, trends, or recommendations..."
                }
                onChange={(e) => setAiPrompt(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault()
                    onAskAI()
                  }
                }}
              />
              <div className="composer-actions">
                <div className="composer-mode">
                  <span>Response</span>
                  <Select
                    size='small'
                    value={aiGenerateImage ? 'image' : 'text'}
                    disabled={aiLoading}
                    // The composer sits at the very bottom of the fixed Ask AI
                    // panel, so open the dropdown upwards to keep it on screen.
                    placement='topLeft'
                    onChange={(value) => setAiGenerateImage(value === 'image')}
                  >
                    <Option value='image' label='Image'>
                      Image
                    </Option>
                    <Option value='text' label='Text'>
                      Text
                    </Option>
                  </Select>
                </div>
                <Button type="primary" loading={aiLoading} onClick={onAskAI}>
                  Send
                </Button>
              </div>
            </div>
          </AskAIPanel>
        )}
        <Banner
          person={person}
          period={arDetail.time_period}
          onThemeChange={handleThemeChange}
          themeChanging={themeChanging}
        />
        <IntroductionSection
          {...commonProps}
          notes={arDetail.notes}
          handleCommendSave={handleCommendSave}
          editabled
        />
        <PersonalInfoSection
          {...{
            person,
            latest_bmi,
            latest_height,
            latest_weight,
            device_info,
            residence
          }}
        />
        {useAspirationAndGoals && <AspirationsGoalsSection {...commonProps} />}
        <HealthCompassSection
          {...commonProps}
          {...{
            compareData,
            dataList,
            person,
            dynamicData
          }}
        />
        {clientGender === 'female' && (
          <Menopause
            {...commonProps}
            {...{
              compareData,
              dataList,
              person,
              dynamicData
            }}
          />
        )}
        {infinityLoadCount > 1 && (
          <Cardiometabolic
            {...commonProps}
            {...{
              previous,
              latest,
              save,
              handleSave,
              trendMetrics,
              useAspirationAndGoals
            }}
          />
        )}

        {infinityLoadCount > 2 && (
          <>
            <BoneHealth
              {...commonProps}
              {...{
                ageScopes,
                compareData,
                dataList,
                save,
                handleSave,
                infinityLoadCount
              }}
            />
            <BodyCompResultSection
              {...commonProps}
              {...{
                ageScopes,
                compareData,
                dataList,
                save,
                handleSave,
                infinityLoadCount
              }}
            />
          </>
        )}

        {infinityLoadCount > 3 && (
          <CognitiveHealth
            {...commonProps}
            {...{
              save,
              dataList,
              compareData,
              ageScopes
            }}
          />
        )}

        {infinityLoadCount > 4 && (
          <Balance
            {...commonProps}
            {...{
              ageScopes,
              compareData,
              dataList,
              save,
              handleSave,
              infinityLoadCount
            }}
          />
        )}

        {infinityLoadCount > 5 && (
          <>
            <MovementQuality
              {...commonProps}
              {...{
                ageScopes,
                compareData,
                dataList,
                save,
                handleSave
              }}
            />
          </>
        )}

        {infinityLoadCount > 6 && (
          <>
            <MuscleSection
              {...commonProps}
              {...{
                ageScopes,
                compareData,
                dataList,
                save,
                handleSave
              }}
            />
          </>
        )}

        {infinityLoadCount > 7 && (
          <Aerobic
            {...commonProps}
            {...{
              ageScopes,
              compareData,
              dataList,
              save,
              handleSave,
              infinityLoadCount
            }}
          />
        )}

        {!hasMore && (
          <>
            {useAIRecommendations ? (
              <>
                <LifeStylePresentation
                  {...commonProps}
                  arDetail={arDetail}
                  person={person}
                  lsd={_.get(dynamicData, 'lifeStyleSummary', {})}
                  editabled={true}
                  handleCommendSave={handleCommendSave}
                />
                <RecommendationSection
                  {...commonProps}
                  person={person}
                  lsd={_.get(dynamicData, 'lifeStyleSummary', {})}
                  hsd={_.get(dynamicData, 'healthSpanDomainSummary', {})}
                  editabled={true}
                  arDetail={arDetail}
                  onUpdateNotes={handleCommendSave}
                  onUpdateReferral={onUpdateReferral}
                  updateArDetail={updateArDetail}
                  exerciseRecommendationPlots={exerciseRecommendationPlots}
                  copilotDashboard={copilotDashboard}
                  useAspirationAndGoals={useAspirationAndGoals}
                  executiveSummary={executiveSummary}
                />
              </>
            ) : (
              <RecommendationSectionV1
                {...commonProps}
                editabled={true}
                arDetail={arDetail}
                onUpdateNotes={handleCommendSave}
                onUpdateReferral={onUpdateReferral}
                updateArDetail={updateArDetail}
                useAspirationAndGoals={useAspirationAndGoals}
              />
            )}
          </>
        )}

        <Footer branded={branded}>
          <div>
            <Button
              ghost
              type="primary"
              onClick={() => handleSave()}
              disabled={editingCount > 0}
            >
              SAVE AS DRAFT
            </Button>
            <Button
              ghost
              type="primary"
              disabled={editingCount > 0}
              onClick={() => showActivity()}
            >
              ACTIVITY
            </Button>
          </div>
          <div>
            <Button
              ghost
              type="primary"
              onClick={handlePreview}
              disabled={editingCount > 0}
            >
              <ViewIcon />
              PREVIEW
            </Button>
            <Button
              type="primary"
              onClick={handlePublish}
              disabled={editingCount > 0}
            >
              <SimpleUploadIcon />
              PUBLISH
            </Button>
            {/* <Button
            type="primary"
            onClick={handleNotify}
            disabled={!arDetail.published_at}
          >
            <BellIcon />
            NOTIFY CLIENT
          </Button> */}
          </div>
        </Footer>
      </DraftContainer>
    </InfiniteScroll>
  ) : createdFailed ? (
    <ErrorContainer>
      <span className="message">{createdFailed}</span>
      <Button
        type="primary"
        onClick={() => {
          props.history.goBack()
        }}
      >
        Back
      </Button>
    </ErrorContainer>
  ) : (
    <PageLoading />
  )
}
